import { ComponentProps, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  composeValidators,
  isEmail,
  isPhone,
  isRequired,
  phoneNumberMask,
  phoneNumberUnmask,
} from '@belong/common';
import { IconRadioBtnSelected } from '@belong/icons';
import { Form, Modal, Text, TextField, Spinner } from '@belong/ui';
import { UserModel } from 'src/api/models';
import { LoginProviders, useLogout } from 'src/features/auth/queries';
import {
  useUser,
  useMutateUser,
  useUpdateUserProfile,
  ContactInfo,
} from 'src/features/users/useUser';
import { useStore } from 'src/store';
import { clearModalsSelector } from 'src/store/modal';
import { AgreeToTermsFooter } from '../agree-to-terms-footer/agree-to-terms-footer';
import { STRINGS } from '../auth.strings';
import css from './enter-contact-modal.module.css';
import { getTitleByPathName } from './utils';

export type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  // `void | Promise<void>`: the sign-up modal forwards its own
  // `(user) => void` prop here, and this component awaits the result either
  // way.
  onSuccessfulLogin?: (user: UserModel) => void | Promise<void>;
  provider: LoginProviders;
  shouldClearModals?: boolean;
  onFinish?:
    | ((hasError: boolean) => Promise<void>)
    | ((hasError: boolean) => void);
  overlayClassName?: string;
  size?: ComponentProps<typeof Modal>['size'];
  closeButton?: ComponentProps<typeof Modal>['closeButton'];
  onOpenCallback?: () => void;
  canDismiss?: boolean;
};

const PHONE_IN_USE_ERROR_FRAGMENT =
  'is already related to a another user account';

export function EnterContactModal({
  isOpen,
  onDismiss,
  onSuccessfulLogin,
  provider,
  shouldClearModals = true,
  onFinish = async () => {},
  overlayClassName = 'z-20',
  size = 'fullScreen',
  closeButton = null,
  onOpenCallback,
  canDismiss = false,
}: Props) {
  const { pathname } = useRouter();
  const hasError = useRef(false);
  const { user } = useUser();
  const { isLoading: isUserLoading, mutateAsync: fetchUser } = useMutateUser();
  const {
    isError,
    error: updateUserError,
    isLoading: isUpdateProfileLoading,
    mutateAsync: updateUserProfile,
  } = useUpdateUserProfile();
  const { isLoading: isLogoutLoading, mutateAsync: logout } = useLogout();
  const clearModals = useStore(clearModalsSelector);
  const isLoading = isUserLoading || isUpdateProfileLoading || isLogoutLoading;
  const isPhoneInUseError =
    updateUserError instanceof Error &&
    updateUserError.message.includes(PHONE_IN_USE_ERROR_FRAGMENT);
  const initialValues: ContactInfo = {
    email: user?.email || '',
    phone: user?.phone || '',
  };

  useEffect(() => {
    if (isOpen) {
      onOpenCallback?.();
    }
  }, [isOpen, onOpenCallback]);

  async function handleDismiss() {
    if (!canDismiss) return;

    try {
      await logout();
    } catch (e) {}
    onDismiss();
  }

  async function handleSubmit(values: ContactInfo) {
    if (!user) return;

    try {
      const userProfile = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user?.email || values.email,
        phone: user?.phone || values.phone,
      };

      await updateUserProfile(userProfile);
      const updatedUser = await fetchUser();

      if (onSuccessfulLogin) {
        await onSuccessfulLogin(updatedUser);
      }

      hasError.current = false;

      if (shouldClearModals) clearModals();
    } catch (e) {
      console.error(e);

      hasError.current = true;
    } finally {
      await onFinish(hasError.current);
    }
  }

  const title = getTitleByPathName(pathname);
  return (
    <>
      <Modal
        name="enter_contact"
        isOpen={isOpen}
        onDismiss={handleDismiss}
        overlayClassName={overlayClassName}
        size={size}
        closeButton={closeButton}
      >
        {isLoading && <Spinner />}
        <div className="px-2sm py-2xl md:py-xl flex flex-col items-center">
          <Text
            variant="h2"
            fontWeight="semibold"
            className="pb-sm max-w-2xl text-center"
          >
            {title}
          </Text>
          {isError && (
            <Text
              variant="p1"
              fontWeight="semibold"
              className="py-xs text-red text-center"
            >
              {isPhoneInUseError
                ? STRINGS['enter_contant.phone_already_exists']
                : STRINGS['enter_contant.contact_update_failed']}
            </Text>
          )}
          <div className="mb-xl pt-sm flex items-center justify-center">
            <IconRadioBtnSelected width={20} className="mr-xs" />
            <Text fontWeight="semibold">
              {STRINGS['enter_contact.subtitle']} {provider}
            </Text>
          </div>
          <Form initialValues={initialValues} onSubmit={handleSubmit}>
            {() => {
              return (
                <div className={css.formWrapper}>
                  {!user?.email && (
                    <div className="mb-sm w-full">
                      <TextField
                        label="Email address"
                        name="email"
                        validate={isEmail}
                      />
                    </div>
                  )}
                  {!user?.phone && (
                    <div className="mb-sm w-full">
                      <TextField
                        label="Phone"
                        name="phone"
                        validate={composeValidators(isRequired, isPhone)}
                        mask={phoneNumberMask}
                        unmask={phoneNumberUnmask}
                      />
                    </div>
                  )}
                  <div className="mb-lg pt-sm">
                    <Form.SubmitButton size="fluid">
                      {STRINGS['enter_contact.cta']}
                    </Form.SubmitButton>
                  </div>
                </div>
              );
            }}
          </Form>
          <AgreeToTermsFooter target="_blank" />
        </div>
      </Modal>
    </>
  );
}
